<script>
    import date from '../lib/date';
    import modals from '../state/modals';

    // Table contents
    export let events;

    // Displayed week of year
    export let weekStartDate;

    // Start from monday
    export let startDay = 1;

    // End on friday
    export let endDay = 5;

    let mappedEvents = {};
    let usedHours = {};
    function mapEvents(list) {
        let out = {};
        usedHours = {};

        if (!list.length) return out;

        usedHours['_'] = true;

        for (let ev of list) {
            let d = date.toDate(ev.evr_from);
            let day = d.getDate();
            let hour = d.getHours();

            if (!out.hasOwnProperty(day)) {
                out[day] = {};
            }

            if (!out[day].hasOwnProperty(hour)) {
                out[day][hour] = [];
            }

            for (let i = 0; i < Math.ceil((ev.mins + ev.length) / 60); i++) {
                usedHours[hour + i] = true;
            }

            out[day][hour].push(ev);
        }

        return out;
    }

    // Pre-process events (map to day, start hour)
    $: mappedEvents = mapEvents(events);

    let days = [];
    function generateDays(dateStart) {
        let out = [];

        let d = dateStart;

        for (let day = startDay; day <= endDay; day++) {
            // Set the date to current day for this iteration
            d = date.setDay(d, day);

            // Add day to list
            out.push({
                name: date.format(d, date.FORMAT_WEEKDAY_NAME).replace(/^./, e => e.toUpperCase()),
                date: date.format(d, date.FORMAT_DATE_HUMAN),
                number: d.getDate()
            });
        }

        return out;
    }

    // Get days of week
    $: days = generateDays(weekStartDate);

    let hours = [];
    function generateHours(events) {
        let out = [];

        // Default ?!
        if (!events.length) return hoursInRange(9, 18);

        // Get ranges based on event hours
        let ranges = hourRanges(usedHours);

        // Get all hours in range
        for (let h = ranges.min; h <= ranges.max; h++) {
            out.push(h);
        }

        // TODO: Use ranges.list

        // document.documentElement.style.setProperty('--hours', out.length);

        return out;
    }

    $: hours = generateHours(events);

    function hourRanges(hours) {
        let ranges = [];
        let min, max, currentRange = null;

        for (let hour in hours) {
            if (!hours.hasOwnProperty(hour) || hour === '_') continue;

            hour = parseInt(hour);

            if (!min) min = hour;
            max = hour;

            // Got a range ?
            if (currentRange) {
                // Get the range start hour
                let rangeStart = currentRange[0];

                // Current hour is not right, close the range off
                if (hour !== rangeStart + 1) {
                    // Range does not have an end, use the start again
                    if (currentRange.length < 2) currentRange.push(rangeStart);

                    // Add range to list and unassign it as current
                    ranges.push(currentRange);
                    currentRange = null;
                } else {
                    // Update range end
                    currentRange[1] = hour;
                }
            }

            // Create new range, if there's none
            if (!currentRange) {
                currentRange = [hour];
                continue;
            }
        }

        // Close off current range
        if (currentRange) {
            if (currentRange.length < 2) currentRange.push(currentRange[0]);

            ranges.push(currentRange);
        }

        return {
            max, min,
            list: ranges
        };
    }

    function hoursInRange(start, stop) {
        let out = [];

        for (let i = start; i <= stop; i++) out.push(i);

        return out;
    }

    // Gets events for specific hour of day
    function getEvents(list, day, hour) {
        if (!list.hasOwnProperty(day)) return [];

        let hours = list[day];
        if (!hours.hasOwnProperty(hour)) return [];

        return hours[hour];
    }

    // Calculates percentage based on the number of minutes
    function minutesToPercents(mins) {
        return (100 / 60) * mins;
    }

    // Gets styles for each event element in the table
    function getEventStyles(ev) {
        return `margin-left: ${minutesToPercents(ev.mins)}%; width: ${minutesToPercents(ev.length)}%;`;
    }

    // Prepares cell width calculation
    let firstColWidth = 120;
    function cellWidth(hours) {
        return `calc((100% - ${firstColWidth}px) / ${hours.length})`;
    }

    function onEventClick(event) {
        modals.toggleEventModal(event);
    }

    function getTimeRange(ev) {
        return [
            date.format(ev.evr_from, date.FORMAT_TIME_HUMAN),
            date.format(ev.evr_to, date.FORMAT_TIME_HUMAN)
        ].join(' - ');
    }

    function getReserves(ev) {
        return `${ev.reserved} / ${ev.evn_max || 'neomezeno'}` + (ev.evn_min ? ` (min: ${ev.evn_min})` : '');
    }
</script>

<style>
    .timetable {
        background: white;
        overflow: auto;
    }

    .timetable-header, .timetable-day {
        display: flex;
        flex-direction: row;
    }

    .header-span, .day-span {
        /* flex: 1; */
        /*width: 10%;*/
        border: 1px solid #eaeaea;
        border-right-width: 0;
        /* width: calc((100% - 120px) / var(--hours)); */
    }

    .header-span:last-child, .day-span:last-child {
        border-right-width: 1px;
    }

    .header-span:first-child, .day-span:first-child {
        width: 120px;
        min-width: 120px;
    }

    .day-span {
        padding: 10px 0;
    }

    .day-span:first-child {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
    }

    .header-span:not(:first-child), .day-span:not(:first-child) {
        min-width: 90px;
    }

    .day-span:not(:first-child) {
        --clr: #f5f5f5;
        background: repeating-linear-gradient(
            135deg,
            white,
            white 2px,
            var(--clr) 4px,
            var(--clr) 4px
        );
    }

    .header-span, .day-span:first-child {
        padding: 5px 8px;
    }

    .event {
        cursor: pointer;
        position: relative;
        padding: 2px 8px;
        font-size: 12px;
        background: #ecf0ff;
        color: #333;
    }

    .event._canceled {
        background: #dcdcdc;
        color: #5d5d5d;
    }

    .event._moved {
        background: #f5e591;
    }

    .event .event-title {
        font-weight: bold;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .event:hover {
        box-shadow: 0 0 6px #dadada;
    }
</style>

<div class="timetable">
    <div class="timetable-header">
        <span class="header-span">&nbsp;</span>

        {#each hours as hour}
            <span class="header-span" style="width: {cellWidth(hours)}">
                {hour}:00 - {hour + 1}:00
            </span>
        {/each}
    </div>

    {#each days as day}
        <div class="timetable-day">
            <span class="day-span">
                <b>{day.name}</b>
                {day.date}
            </span>

            {#each hours as hour}
                <span class="day-span" style="width: {cellWidth(hours)}">
                    {#each getEvents(mappedEvents, day.number, hour) as ev}
                        <div class="event" style="{getEventStyles(ev)}"
                             class:_canceled={ev.evr_canceled > 0}
                             class:_moved={ev.evr_original_from?.length}
                             title="{ev.evn_title} ({getTimeRange(ev)})"
                             on:click={() => onEventClick(ev)}>
                            <div class="event-title">{ev.evn_title}</div>
                            {#if ev.evr_original_from}
                                <div class="event-moved">
                                    <i class="fa fa-exclamation fa-fw"></i> posunuto z {date.format(ev.evr_original_from, date.FORMAT_TIME_HUMAN)}
                                </div>
                            {/if}
                            {#if ev.ven_id}
                                <div class="event-venue">
                                    <i class="fa fa-house fa-fw"></i> {ev.ven_shortCaption}
                                </div>
                            {/if}
                            {#if ev.evr_canceled > 0}
                                <div class="event-cancelled">Zrušena</div>
                            {:else}
                                <div class="event-capacity">
                                    <i class="fa fa-users fa-fw"></i> {getReserves(ev)}
                                </div>
                            {/if}
                        </div>
                    {/each}
                </span>
            {/each}
        </div>
    {/each}
</div>